import { useEffect, useState } from "react";
import "./LogoGrid.css";
import { ReactComponent as PeacockLogo } from "assets/peacock.svg";
import { ReactComponent as BreakLogo } from "assets/break.svg";

const logos = [...Array(26).fill(<PeacockLogo />)];
const DELAY_STEP = 50; // ms
const DISPLAY_TIME = 2000; // Temps d'affichage après animation d'entrée
const ANIMATION_DURATION = 1000; // fadeIn/fadeOut dure 1s
const CYCLE_DURATION =
	DELAY_STEP * logos.length +
	DISPLAY_TIME +
	DELAY_STEP * logos.length +
	ANIMATION_DURATION;

interface LogoGridProps {
	isVisible: boolean;
}

const LogoGrid = ({ isVisible }: LogoGridProps) => {
	const [animationCycle, setAnimationCycle] = useState(0);

	useEffect(() => {
		if (!isVisible) {
			return;
		}

		const interval = setInterval(() => {
			setAnimationCycle((prev) => prev + 1);
		}, CYCLE_DURATION);

		return () => clearInterval(interval);
	}, [isVisible]);

	if (!isVisible) {
		return null;
	}

	const rows = [
		{ start: 0, count: 6, breakOffset: 1 },
		{ start: 6, count: 7, breakOffset: 0 },
		{ start: 13, count: 6, breakOffset: 1 },
		{ start: 19, count: 7, breakOffset: 0, hideMiddle: true },
	];

	const totalLogos = logos.length;
	let globalIndex = 0;

	const renderRow = (
		rowIndex: number,
		count: number,
		hideMiddle = false,
		breakOffset = 0,
	) => {
		return (
			<div className="logo-row" key={`row-${rowIndex}`}>
				{Array.from({ length: count }).map((_, colIndex) => {
					const isHidden = hideMiddle && colIndex === Math.floor(count / 2);

					if (isHidden) {
						globalIndex++;
						return (
							<div
								key={`placeholder-${globalIndex}`}
								className="logo-placeholder"
							/>
						);
					}

					const appearDelay = globalIndex * DELAY_STEP;
					const disappearDelay = (totalLogos - 1 - globalIndex) * DELAY_STEP;

					const key = `${animationCycle}-${globalIndex}`;
					const logoElement = logos[globalIndex];
					globalIndex++;

					return (
						<div key={key} className="logo-wrapper">
							<div
								className="fade-in"
								style={{ animationDelay: `${appearDelay}ms` }}
							>
								<div
									className="fade-out"
									style={{
										animationDelay: `${DISPLAY_TIME + disappearDelay}ms`,
									}}
								>
									<div className="logo-icon-wrapper">
										{colIndex % 2 === breakOffset && (
											<BreakLogo
												className="break-logo"
												style={{ animationDelay: `${appearDelay + 200}ms` }}
											/>
										)}
										{logoElement}
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	return (
		<div className="logo-grid" key={`grid-${animationCycle}`}>
			{rows.map((row, i) =>
				renderRow(i, row.count, row.hideMiddle, row.breakOffset),
			)}
		</div>
	);
};

export default LogoGrid;
